// Central permission helpers for the admin panel.
//
// Mirrors backend/src/config/permissionCatalog.ts: a user's effective
// permissions are stored on `admin_user` in localStorage (set at login and
// refreshed from /auth/me). All UI gating must go through these helpers so
// the client fails closed (no stored user => no permissions) and stays
// consistent with the backend route guards.

export interface StoredUser {
  id?: string;
  email?: string;
  name?: string;
  role?: string;
  department?: string;
  position?: string;
  permissions?: string[];
  status?: string;
}

/** Parse the cached admin user. Returns null when absent or malformed. */
export function getStoredUser(): StoredUser | null {
  try {
    const raw = localStorage.getItem('admin_user');
    return raw ? (JSON.parse(raw) as StoredUser) : null;
  } catch {
    return null;
  }
}

/** Effective permissions for the cached user (empty when unknown). */
export function getPermissions(): string[] {
  const user = getStoredUser();
  return Array.isArray(user?.permissions) ? user!.permissions! : [];
}

/** True when the cached user holds `permission`. Fails closed. */
export function hasPermission(permission: string): boolean {
  return getPermissions().includes(permission);
}

/** True when the cached user holds at least one of `permissions`. Fails closed. */
export function hasAnyPermission(...permissions: string[]): boolean {
  const held = getPermissions();
  return permissions.some((permission) => held.includes(permission));
}

/** True when the cached user's role is one of `roles`. Fails closed. */
export function hasRole(...roles: string[]): boolean {
  const role = getStoredUser()?.role;
  return !!role && roles.includes(role);
}

/**
 * Money figures (prices, totals, revenue) are visible only to roles that hold
 * a money-handling line: billing, reporting, revenue, or POS order entry
 * (you cannot take an order without seeing prices). This mirrors the backend,
 * which redacts order/menu money fields for the kitchen role — kitchen holds
 * none of these permissions. Fails closed when the user cannot be resolved.
 */
export function canViewPrices(): boolean {
  return hasAnyPermission(
    'billing:view',
    'reports:view',
    'dashboard:revenue',
    'restaurant:orders',
  );
}
